import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { RegistrationRequest } from "src/app/view-models/registration-request";

// Identity carried over from the Auth0 link flow when "Create a new account" runs the registration
// wizard. When `active`, the wizard prefills/locks these fields and links the new account to Auth0.
export interface Auth0RegistrationContext {
	active: boolean;
	email: string;
	firstName: string;
	lastName: string;
}

@Injectable({
	providedIn: 'root'
})
export class RegistrationService {
	private registrationInformation: RegistrationRequest;
	private currentActiveIndex: number = 0;
	private isRegistrationComplete: boolean = false;
	private auth0Context: Auth0RegistrationContext | null = null;
	// Persisted in sessionStorage so the Auth0 "create a new account" flow survives a page refresh
	// (the in-memory wizard state would otherwise be lost and the page would look like a normal
	// public registration). sessionStorage keeps it scoped to the current tab/session.
	private readonly auth0ContextStorageKey = 'auth0RegistrationContext';
	// Set when the just-completed registration was the Auth0 create-and-link flow, so the success
	// page can show an Auth0-appropriate message (no set-password email is sent in that case).
	private isAuth0Registration: boolean = false;

	private indexSubject: Subject<number> = new Subject<number>();
  private indexObservable: Observable<number> = this.indexSubject.asObservable();

	constructor() { }

	public updateInfo(registrationRequest: RegistrationRequest) {
		this.registrationInformation = {...this.registrationInformation, ...registrationRequest};
	}

	public clearInformation() {
		this.registrationInformation = new RegistrationRequest();
		this.clearAuth0Context();
	}

	public setAuth0Context(context: Auth0RegistrationContext) {
		this.auth0Context = context;
		try {
			sessionStorage.setItem(this.auth0ContextStorageKey, JSON.stringify(context));
		} catch { /* sessionStorage unavailable (e.g. SSR) — in-memory value still applies */ }
	}

	public getAuth0Context(): Auth0RegistrationContext | null {
		// Re-hydrate from sessionStorage after a page refresh, when the in-memory value is gone.
		if (!this.auth0Context) {
			try {
				const raw = sessionStorage.getItem(this.auth0ContextStorageKey);
				if (raw) {
					this.auth0Context = JSON.parse(raw);
				}
			} catch { /* sessionStorage unavailable or bad JSON — treat as no context */ }
		}
		return this.auth0Context;
	}

	public clearAuth0Context() {
		this.auth0Context = null;
		try {
			sessionStorage.removeItem(this.auth0ContextStorageKey);
		} catch { /* sessionStorage unavailable — nothing to clear */ }
	}

	public setIsAuth0Registration(value: boolean) {
		this.isAuth0Registration = value;
	}

	public getIsAuth0Registration() {
		return this.isAuth0Registration;
	}

	public getRegistrationInformation() {
    return this.registrationInformation;
  }

	public setCurrentActiveIndex(activeIndex: number) {
    this.currentActiveIndex = activeIndex;
  }

	public getCurrentActiveIndex() {
    return this.currentActiveIndex;
  }

	public setIndex(index: number) {
    this.indexSubject.next(index);
  }

	public getIndexAsObservable() {
    return this.indexObservable;
  }

	public getIsRegistrationComplete() {
    return this.isRegistrationComplete;
  }

	public setIsRegistrationComplete(isCompleted: boolean) {
    this.isRegistrationComplete = isCompleted;
  }
}
